import { test as fuzzyTest } from "fuzzy";
import { IconWalls } from "$root/components/IconWall";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { UNIVERSE_INSTRUMENT_LIMIT, UNIVERSE_WITH_GROUPS_GROUP_LIMIT } from "$root/functional-areas/const";
import { getTotalAllocationWeight, normalise } from "$root/functional-areas/instruments-editor/builder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { spawnInstrumentAddersDialog } from "$root/functional-areas/instruments-editor/spawn/instrument-adder";
import { InstrumentPool } from "$root/functional-areas/instruments-editor/spawn/instrument-adder/store";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { commonColumnDataDefinition } from "$root/ui-lib/table/column/common-data-definitions";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { sumArrayLike } from "$root/utils";
import {
	SelectableBasketBasketTypeEnum,
	type GroupInstrumentUniverseRequest,
	type InstrumentGroupRequest,
	type UserInstrumentDto,
} from "@eu.mdotm.api/sphere";
import {
	ActionText,
	AutoTooltip,
	Banner,
	Column,
	DebouncedSearchInput,
	Icon,
	IconTooltip,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { generateUniqueDOMId, type ImperativeHandleRefProps } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { fuzzyFilterSort, noop } from "@mdotm/shadcn/legacy/utils";
import type { ColDef, IAggFuncParams, RowClassRules } from "ag-grid-community";
import BigNumber from "bignumber.js";
import { Map, OrderedMap, Set } from "immutable";
import { useRef, useMemo, useState, useImperativeHandle } from "react";
import { decimalPlaces, step } from "./editor-config";
import { SidePanelListItem } from "$root/functional-areas/workspace/ui/SidePanelItem";
import { Divider } from "$root/components/Divider";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { BulkActions, Button } from "@mdotm/shadcn/mdotm/components";
import { formatNumber } from "$root/localization/formatters";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";

export type UniverseGroup = InstrumentGroupRequest & {
	localUuid?: string;
};

function getGroupId(group: UniverseGroup): string | null {
	return group.uuid ?? group.localUuid ?? null;
}

export type UniverseWithGroupsCardContentHandle = {
	getSelectedGroupUuid(): string | null;
};

export function UniverseWithGroupsCardContent(
	props: {
		initialSelectedGroupUuid?: string;
	} & ImperativeHandleRefProps<UniverseWithGroupsCardContentHandle> &
		(
			| ({
					mode: "read";
					variant?: "selection" | "details";
			  } & (
					| {
							groups: InstrumentGroupRequest[];
							universe?: undefined;
					  }
					| {
							groups?: undefined;
							universe: GroupInstrumentUniverseRequest;
					  }
			  ))
			| {
					mode: "edit";
					groups: UniverseGroup[];
					onChangeGroups(groups: UniverseGroup[]): void;
					errorMessage?: string;
			  }
		),
): React.JSX.Element {
	const groups: UniverseGroup[] = props.mode === "edit" ? props.groups : (props.groups ?? props.universe.groups!);

	const [selectedGroupUuid, setSelectedGroupUuid] = useState<string | null>(props.initialSelectedGroupUuid ?? null);
	useImperativeHandle(
		props.handleRef,
		() => ({
			getSelectedGroupUuid: () => selectedGroupUuid,
		}),
		[selectedGroupUuid],
	);

	if (
		(selectedGroupUuid == null && groups.length > 0) ||
		(selectedGroupUuid != null && !groups.some((g) => getGroupId(g) === selectedGroupUuid))
	) {
		const firstGroup = groups.at(0);
		if (!firstGroup) {
			setSelectedGroupUuid(null);
		} else {
			setSelectedGroupUuid(getGroupId(firstGroup));
		}
	}

	// select and read are the same
	const columns = columnsDefinition.useInstrumentComposition(props.mode);

	const [groupQuery, setGroupQuery] = useState("");
	const [instrumentQuery, setInstrumentQuery] = useState("");

	const tickerInfoQueryRaw = instrumentsRepository.tickersInfo.useQuery(
		groups.flatMap((g) => (g.instruments ?? []).flatMap((instr) => (instr.tickerName ? [instr.tickerName] : []))),
	);
	const tickerInfoQuery = useMappedQuery(
		(tickersInfo) => Map(tickersInfo.map((info) => [info.tickerName!, info])),
		[],
		tickerInfoQueryRaw,
	);

	const filteredGroups = useMemo(() => {
		const filtered = fuzzyFilterSort(groups, groupQuery, (group) => group.name ?? "");
		return !instrumentQuery
			? filtered
			: filtered.filter((group) =>
					group.instruments?.some((instr) => {
						const row = tickerInfoQuery.data?.get(instr.tickerName!);
						return row && fuzzyTest(instrumentQuery, tickerInfoToString(row));
					}),
				);
	}, [groups, groupQuery, instrumentQuery, tickerInfoQuery.data]);

	const selectedGroupIndex = useMemo(
		() => groups.findIndex((g) => getGroupId(g) === selectedGroupUuid),
		[groups, selectedGroupUuid],
	);
	const filteredSelectedGroupIndex = useMemo(
		() => filteredGroups.findIndex((g) => getGroupId(g) === selectedGroupUuid),
		[filteredGroups, selectedGroupUuid],
	);

	// could've been a useMemo, but with this ref we
	// can optimize for the most common use case where only
	// one group is edited at a time, making the computation
	// much faster.
	const prevGroupsRef = useRef<UniverseGroup[]>([]);
	const isSum100ByGroupRef = useRef(Map<string, boolean>());

	if (prevGroupsRef.current !== groups) {
		const prevById: Record<string, UniverseGroup> = Object.fromEntries(
			prevGroupsRef.current.map((g) => [getGroupId(g) ?? "", g]),
		);
		const prevResult = isSum100ByGroupRef.current;
		isSum100ByGroupRef.current = Map(
			groups.map((g): [string, boolean] => {
				const id = getGroupId(g) ?? "";
				if (prevById[id]?.instruments === g.instruments && prevResult.has(id)) {
					return [id, prevResult.get(id)!];
				}
				const groupInstruments = g.instruments ?? [];
				const sum = getTotalAllocationWeight(groupInstruments.map((instr) => instr.weight ?? 0));
				return [id, Math.abs(sum.toNumber() - 100) < step || groupInstruments.length === 0];
			}),
		);
		prevGroupsRef.current = groups;
	}

	const isSum100ByGroup = isSum100ByGroupRef.current;

	return (
		<Row minSize="0" gap={16}>
			<Column minSize="0" flexGrow={0} gap={8} className="max-w-56">
				<DebouncedSearchInput
					leftContent={<Icon icon="Search" />}
					externalQuery={groupQuery}
					onChange={setGroupQuery}
					placeholder="Filter by group name"
				/>
				<Row flexGrow={0} alignItems="center">
					<Text as="div" className="px-2" type="Body/M/Book">{`Showing ${filteredGroups.length} of ${
						groups.length
					} ${groups.length === 1 ? "group" : "groups"}`}</Text>
					{props.mode === "edit" && (
						<Button
							className="ml-auto"
							size="x-small"
							palette="secondary"
							disabled={groups.length >= UNIVERSE_WITH_GROUPS_GROUP_LIMIT}
							onClick={() => {
								spawnCreateDialog({
									entityType: "Group",
									inputLabel: "Group name",
									// async checkIfNameIsAvailable(name) {
									// 	return !groups.some((g) => g.name?.trim().toLowerCase() === name.trim().toLowerCase());
									// },
									onSubmitAsync: (name) => {
										const localUuid = `local-${generateUniqueDOMId()}`;
										props.onChangeGroups([
											{
												localUuid,
												name,
												instruments: [],
											},
											...groups,
										]);
										setSelectedGroupUuid(localUuid);
									},
								});
							}}
						>
							<Icon icon="Outline1" size={16} color={themeCSSVars.palette_P600} />
						</Button>
					)}
				</Row>

				<Row flexGrow={0} alignItems="center" className="mt-2 mb-1">
					<Text as="div" className="px-2" type="Body/L/Bold">
						{props.mode === "read" && props.variant === "selection" ? "Added groups" : "Groups"}
					</Text>
					{props.mode === "edit" && props.errorMessage && (
						<IconTooltip iconSize={16} severity="warning" overrideIcon="Alert">
							{props.errorMessage}
						</IconTooltip>
					)}
				</Row>

				<Column minSize="0" flexGrow={0} className="overflow-y-auto" dir="rtl">
					{/* dir="rtl" -> dir="ltr" hack to have the scrollbar on the left side */}
					<Column dir="ltr" flexGrow={0} gap={8}>
						<ForEach
							collection={filteredGroups}
							keyProvider={(g) => getGroupId(g) ?? ""}
							fallback={() =>
								groupQuery || instrumentQuery ? <div className="px-2 italic">No group found</div> : null
							}
						>
							{(group) => {
								const groupId = getGroupId(group);
								return (
									<SidePanelListItem
										size="small"
										selected={groupId === selectedGroupUuid}
										title={group.name ?? ""}
										count={group.instruments?.length}
										onClick={() => setSelectedGroupUuid(groupId)}
										dotColor={
											props.mode !== "edit" || (isSum100ByGroup.get(groupId ?? "") ?? true)
												? undefined
												: themeCSSVars.palette_W500
										}
										overlayActions={
											props.mode === "edit"
												? [
														{
															icon: "Delete",
															onClick: () => {
																const onDelete = () => {
																	const groupIndex = groups.findIndex((g) => getGroupId(g) === groupId);
																	const next = groups.slice();
																	next.splice(groupIndex, 1);
																	if (groupId === selectedGroupUuid) {
																		const firstGroup = groups.at(0);
																		if (firstGroup) {
																			setSelectedGroupUuid(getGroupId(firstGroup));
																		}
																	}
																	props.onChangeGroups(next);
																};

																if (props.mode === "edit") {
																	if (group.instruments?.length === 0) {
																		onDelete();
																	} else {
																		spawnYesNoDialog({
																			size: "large",
																			header: "Are you sure you want to delete this group?",
																			children:
																				"This action will permanently remove the group and all weights assigned to its instruments.",
																			noButton: "Cancel",
																			yesButton: "Delete",
																		})
																			.then((shouldContinue) => {
																				if (shouldContinue) {
																					onDelete();
																				}
																			})
																			.catch(noop);
																	}
																}
															},
														},
														{
															icon: "Edit",
															onClick: () => {
																spawnRenameDialog({
																	entityType: "Group",
																	inputLabel: "Group name",
																	currentName: group.name ?? "",
																	// async checkIfNameIsAvailable(name) {
																	// 	return !groups.some((g) => g.name?.trim().toLowerCase() === name.trim().toLowerCase());
																	// },
																	onSubmitAsync: (name) => {
																		const groupIndex = groups.findIndex((g) => getGroupId(g) === getGroupId(group));
																		const next = groups.slice();
																		next[groupIndex] = { ...next[groupIndex], name };
																		props.onChangeGroups(next);
																	},
																}).catch(noop);
															},
														},
													]
												: undefined
										}
									/>
								);
							}}
						</ForEach>
					</Column>
				</Column>
			</Column>
			<Divider direction="vertical" />
			<Column gap={16}>
				<Row flexGrow={0} gap={16} justifyContent="space-between">
					<DebouncedSearchInput
						className="basis-md max-w-md"
						leftContent={<Icon icon="Search" />}
						externalQuery={instrumentQuery}
						onChange={setInstrumentQuery}
						placeholder="Filter by instrument name, ISIN, asset class or micro asset class"
					/>

					{props.mode === "read" ? (
						props.universe && (
							<div className="flex space-x-1 items-end grow-0">
								<ActionText
									color={themeCSSVars.palette_N400}
									href={typedUrlForRoute("PortfolioStudioSettings/InstrumentCustomisation", {
										universeUuid: props.universe.uuid,
										universeName: props.universe.name,
										basketType: SelectableBasketBasketTypeEnum.GroupUniverse,
									})}
								>
									Instruments customisation
								</ActionText>

								<AutoTooltip
									position="top"
									align="endToStart"
									mode="hover"
									overrideColor={themeCSSVars.palette_N400}
									trigger={({ innerRef }) => (
										<div ref={innerRef}>
											<Icon icon="info" color={themeCSSVars.palette_N400} size={18} />
										</div>
									)}
								>
									Explore all instruments used within your account, generate descriptions, and customize instrument
									details.
								</AutoTooltip>
							</div>
						)
					) : (
						<Button
							palette="secondary"
							size="small"
							disabled={selectedGroupIndex === -1}
							onClick={() => {
								if (selectedGroupIndex === -1) {
									return;
								}
								const instruments = groups[selectedGroupIndex].instruments ?? [];
								const tickersInGroup = Set(instruments.map((instr) => instr.tickerName));
								spawnInstrumentAddersDialog({
									allowedInstrumentPools: [InstrumentPool.instrumentCustomisation],
									entity: "UNIVERSE",
									uuid: undefined,
									instrumentsAlreadySelected: instruments.map((x) => ({
										id: x.tickerName ?? "",
										ticker: x.tickerName,
										tickerName: x.tickerName,
										instrumentType: "INSTRUMENT",
									})),
									onSubmitAsync: (tickerNames) => {
										const next = groups.slice();
										next[selectedGroupIndex] = {
											...next[selectedGroupIndex],
											instruments: instruments
												.concat(
													tickerNames.flatMap((tickerName) =>
														tickersInGroup.has(tickerName)
															? []
															: [
																	{
																		tickerName,
																		weight: 0,
																	},
																],
													),
												)
												.slice(0, UNIVERSE_INSTRUMENT_LIMIT),
										};
										props.onChangeGroups(next);
									},
								});
							}}
						>
							<Icon icon="Outline1" />
							&nbsp; Add instruments
						</Button>
					)}
				</Row>

				<ReactQueryWrapperBase query={tickerInfoQuery}>
					{(tickers) => (
						<Column minSize="0" flexGrow={1} gap={16} className="overflow-auto px-4 -mx-4 relative">
							{selectedGroupIndex === -1 ? (
								props.mode === "read" && props.variant === "selection" ? (
									<IconWalls.UniverseWithGroupEmptySelection />
								) : (
									<IconWalls.UniverseWithGroupEmpty />
								)
							) : filteredGroups.length === 0 ? (
								<IconWalls.UniverseWithGroupEmptyFilterResult />
							) : (
								<UniverseGroup
									query={instrumentQuery}
									tickersInfo={tickers}
									columns={columns}
									group={
										filteredSelectedGroupIndex === -1
											? groups[selectedGroupIndex]
											: filteredGroups[filteredSelectedGroupIndex]
									}
									{...(props.mode === "read"
										? { mode: "read" }
										: {
												mode: "edit",
												isSum100: isSum100ByGroup.get(getGroupId(groups[selectedGroupIndex]) ?? "") ?? true,
												onDelete: () => {
													const next = groups.slice();
													next.splice(selectedGroupIndex, 1);
													setSelectedGroupUuid(null);
													props.onChangeGroups(next);
												},
												onChangeGroup: (x) => {
													const next = groups.slice();
													next[selectedGroupIndex] = x;
													props.onChangeGroups(next);
												},
											})}
								/>
							)}
						</Column>
					)}
				</ReactQueryWrapperBase>
			</Column>
		</Row>
	);
}

/** @internal — exported for tests */
export const aggFuncs = {
	sumWeights(params: IAggFuncParams<Instrument, any, any>) {
		let sum = 0;
		if (params.rowNode.group) {
			return sumArrayLike(params.rowNode.allLeafChildren ?? [], (row) => {
				if (row.data?.weight == null) {
					return 0;
				}
				return row.data?.deleted ? 0 : row.data.weight;
			});
		}

		params.api.forEachNode((node) => {
			if (!node.data?.deleted) {
				sum += node.data?.weight ?? 0;
			}
		});
		return sum;
	},
};
/** @internal — exported for tests */
export const rowClassRules: RowClassRules = {
	[`bg-[var(--global-palette-warning-100)]`]: (params) => {
		return Boolean(params.data?.someInstrumentsAreExcluded);
	},
	[`bg-[var(--Table-highlightedRowBackgroundColor)]`]: (params) => {
		return Boolean(params.data?.deleted);
	},
};

function tickerInfoToString(row: UserInstrumentDto): string {
	return [
		row.customAttributes,
		row.description,
		...Object.values(row.identifiers ?? {}),
		row.name,
		...(row.qualities ?? []).map((t) => t.value),
		...(row.tags ?? []).map((t) => (t.values ?? []).map((v) => v.option).join(" ")),
		...(row.scores ?? []).map((t) => formatNumber(t.value)),
		row.tickerName,
	].join(" ");
}

function UniverseGroup({
	query,
	columns,
	group,
	tickersInfo,
	...rest
}: {
	query: string;
	tickersInfo: Map<string, UserInstrumentDto>;
	columns: ColDef<Instrument>[];
} & (
	| {
			mode: "edit";
			group: UniverseGroup;
			isSum100: boolean;
			onDelete(): void;
			onChangeGroup(group: UniverseGroup): void;
	  }
	| {
			mode: "read";
			group: UniverseGroup;
	  }
)) {
	const [selection, setSelection] = useState<string[]>([]);
	const instruments = useMemo(() => group.instruments ?? [], [group.instruments]);
	const rows = useMemo(
		() =>
			instruments.map((instr) => {
				const instrumentFromMap = tickersInfo.get(instr.tickerName!);
				return {
					...(instrumentFromMap ?? {}),
					id: instrumentFromMap?.tickerName ?? instr.tickerName!,
					instrumentType: "INSTRUMENT",
					weight: instr.weight,
					tickerName: instrumentFromMap?.tickerName ?? instr.tickerName,
				};
			}),
		[instruments, tickersInfo],
	);

	const { filtered } = useSearchable({
		collection: rows,
		query,
		extractFn: tickerInfoToString,
	});

	const restRef = useUnsafeUpdatedRef(rest);

	const editActions = rest.mode === "edit" && {
		deleteSelected: () => {
			rest.onChangeGroup({
				...group,
				instruments: instruments.filter((instr) => !selection.includes(instr.tickerName!)),
			});
		},
		changeName: (newName: string) => {
			rest.onChangeGroup({
				...group,
				instruments: rows.map((instr) => ({
					tickerName: instr.tickerName ?? "",
					weight: instr.weight,
				})),
				name: newName,
			});
		},
		quickNormalise: () => {
			rest.onChangeGroup({
				...group,
				instruments: normalise({
					allocation: OrderedMap(instruments.map((instr) => [instr.tickerName!, BigNumber(instr.weight ?? 0)])),
					decimalPlaces,
					distributeTo: instruments.map((instr) => instr.tickerName!),
				})
					.toArray()
					.map(([tickerName, weight]) => ({ tickerName, weight: weight.toNumber() })),
			});
		},
		normaliseSelected: () => {
			rest.onChangeGroup({
				...group,
				instruments: normalise({
					allocation: OrderedMap(instruments.map((instr) => [instr.tickerName!, BigNumber(instr.weight ?? 0)])),
					decimalPlaces,
					distributeTo: selection,
				})
					.toArray()
					.map(([tickerName, weight]) => ({ tickerName, weight: weight.toNumber() })),
			});
		},
	};

	const persistColProps = agExtra.usePersistColState(
		rest.mode === "edit"
			? persistedColStateKeys["tableV3-universe-compositionWithGroups-edit"]
			: persistedColStateKeys["tableV3-universe-composition-read"],
	);
	return (
		<Column gap={4}>
			<Text type="Body/L/Bold" as="div" color={themeCSSVars.palette_N700}>
				{group.name}
			</Text>

			<Column flexGrow={1} gap={16}>
				{editActions && (
					<BulkActions
						count={selection.length}
						actions={[
							{
								icon: "Delete",
								label: "Delete",
								disabled: selection.length === 0,
								onClick: editActions.deleteSelected,
							},
							{
								icon: "Percentile",
								label: "Normalise",
								disabled: selection.length === 0,
								onClick: editActions.normaliseSelected,
							},
						]}
					/>
				)}
				<div className="min-h-0 flex-1">
					<AgGridWithModules<Instrument>
						{...(rest.mode === "read" ? {} : agExtra.checkboxColumn())}
						{...persistColProps}
						columnDefs={columns}
						rowData={filtered as Instrument[]}
						dataTypeDefinitions={commonColumnDataDefinition}
						grandTotalRow="pinnedBottom"
						onSelectionChanged={(params) => setSelection(params.api.getSelectedRows().map((x) => x.id))}
						autoSizeStrategy={{ type: "fitGridWidth" }}
						getRowId={(row) => row.data.id}
						onCellValueChanged={(params) => {
							if (params.column.getColId() === "ACTION") {
								(restRef.current as typeof rest & { mode: "edit" }).onChangeGroup({
									...group,
									instruments: instruments.filter((instr) => instr.tickerName !== params.data.tickerName),
								});
								return;
							}

							if (params.column.getColId() === "WEIGHT") {
								(restRef.current as typeof rest & { mode: "edit" }).onChangeGroup({
									...group,
									instruments: instruments.map((instr) =>
										instr.tickerName === params.data.tickerName
											? {
													tickerName: params.data.tickerName,
													weight: params.newValue ?? undefined,
												}
											: instr,
									),
								});
							}
						}}
						rowClassRules={rowClassRules}
						aggFuncs={aggFuncs}
					/>
				</div>

				{editActions && !rest.isSum100 && (
					<Banner title="The total weight of your portfolio does not sum to 100%" severity="warning">
						<Text type="Body/M/Book" as="div" className="mb-2">
							Use{" "}
							<ActionText type="Body/M/Bold" onClick={editActions.quickNormalise}>
								Quick Normalise
							</ActionText>{" "}
							to automatically adjust all weights proportionally so that the total reaches 100%.
						</Text>
						<Text type="Body/M/Book" as="div" className="mb-2">
							Alternatively, select one or more instruments and apply the{" "}
							<ActionText type="Body/M/Bold" disabled={selection.length === 0} onClick={editActions.normaliseSelected}>
								Normalise
							</ActionText>{" "}
							function to rebalance only the selected weights.
						</Text>
					</Banner>
				)}
			</Column>
		</Column>
	);
}
